import React from 'react';
import { Button } from '@material-ui/core';
import { KeyboardArrowDownRounded, PersonAddDisabled, RemoveCircleOutline } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import Icon from '@mdi/react';
import { iconList } from '../../../../../../common/icons/RoleIconList';

type QualificationStatus = 'qualified' | 'missing-tasks' | 'not-qualified' | 'unrestricted';

interface Props {
  status: QualificationStatus;
  originalStatusText?: string;
  originalStatusColor?: string;
  matchingRoleIcon?: string | null;
  onClick?: (e: any) => void;
  disabled?: boolean;
}

const STATUS_CONFIG: Record<QualificationStatus, { label: string; bg: string; color: string; borderColor: string }> = {
  qualified: { label: 'Qualified', bg: '#E8F5E9', color: '#2E7D32', borderColor: '#C8E6C9' },
  'missing-tasks': { label: 'Missing Tasks', bg: '#FFF3E0', color: '#E65100', borderColor: '#FFE0B2' },
  'not-qualified': { label: 'Not Qualified', bg: '#F5F5F5', color: '#9E9E9E', borderColor: '#E0E0E0' },
  unrestricted: { label: '\u2014', bg: '#FAFAFA', color: '#BDBDBD', borderColor: '#EEEEEE' },
};

const SHORT_STATUS_LABELS: Record<string, string> = {
  'No indication': 'NI',
  Available: 'A',
  Rostered: 'R',
  'To be rostered': 'R',
  'To be removed': 'X',
  Unavailable: 'U',
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: 150,
      minWidth: 150,
      overflowX: 'hidden',
      margin: 'auto',
      fontSize: '0.75rem',
      textTransform: 'none',
      padding: '7px 12px',
      height: '40px',
      whiteSpace: 'nowrap',
      fontWeight: 500,
      overflow: 'hidden',
    },
    triangle: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: 20,
      height: 20,
      borderRadius: '0 3px 0 3px',
      clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
      zIndex: 1,
    },
    triangleLabel: {
      position: 'absolute',
      top: 2,
      right: 2,
      fontSize: 8,
      fontWeight: 700,
      lineHeight: 1,
      color: '#fff',
      zIndex: 2,
    },
  })
);

export function RoleQualificationPill({
  status,
  originalStatusText,
  originalStatusColor,
  matchingRoleIcon,
  onClick,
  disabled,
}: Props) {
  const classes = useStyles();
  const config = STATUS_CONFIG[status];
  const shortLabel = originalStatusText ? SHORT_STATUS_LABELS[originalStatusText] || '' : '';
  const getRoleIcon = () => {
    if ((status === 'qualified' || status === 'missing-tasks') && matchingRoleIcon && iconList[matchingRoleIcon]) {
      return <Icon path={iconList[matchingRoleIcon]} size='16px' color={config.color} />;
    }
    if (status === 'not-qualified') {
      return <PersonAddDisabled style={{ fontSize: '16px' }} />;
    }
    if (status === 'unrestricted') {
      return <RemoveCircleOutline style={{ fontSize: '16px' }} />;
    }
    return undefined;
  };

  return (
    <Button
      onClick={onClick}
      variant='outlined'
      disabled={disabled}
      startIcon={getRoleIcon()}
      endIcon={<KeyboardArrowDownRounded style={{ fontSize: '18px' }} />}
      classes={{ root: classes.root }}
      style={{
        backgroundColor: config.bg,
        borderColor: config.borderColor,
        color: config.color,
      }}
    >
      {originalStatusColor && shortLabel && (
        <>
          <span className={classes.triangle} style={{ backgroundColor: originalStatusColor }} />
          <span className={classes.triangleLabel}>{shortLabel}</span>
        </>
      )}
      {config.label}
    </Button>
  );
}

export function getQualificationStatus(
  volunteerRoleIds: string[] | undefined,
  sessionRequiredRoles: Array<{ activityRoleId: string }> | undefined,
  volunteerRoleTasksComplete?: boolean
): QualificationStatus {
  if (!sessionRequiredRoles || sessionRequiredRoles.length === 0) {
    return 'unrestricted';
  }
  if (!volunteerRoleIds) {
    return 'not-qualified';
  }
  const hasMatchingRole = sessionRequiredRoles.some((role) => volunteerRoleIds.includes(role.activityRoleId));
  if (!hasMatchingRole) {
    return 'not-qualified';
  }
  if (volunteerRoleTasksComplete === false) {
    return 'missing-tasks';
  }
  return 'qualified';
}
